import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, ArrowLeft, Layers, Hammer, ShoppingBag, Car, Plane, MoreHorizontal } from "lucide-react";
import { Header } from "@/components/laneguide/Header";
import { Footer } from "@/components/laneguide/Footer";
import { AmountInput } from "@/components/laneguide/AmountInput";
import { FaqSection } from "@/components/laneguide/PageFaq";

export const Route = createFileRoute("/lanetest")({
  head: () => ({
    meta: [
      { title: "Lånetest – Hitta ditt lån på 60 sekunder | Svensk Låneguide" },
      { name: "description", content: "Gör vårt kostnadsfria lånetest. Svara på 4 snabba frågor och få personliga låneförslag." },
      { property: "og:title", content: "Lånetest – Hitta ditt lån på 60 sekunder" },
      { property: "og:description", content: "Kostnadsfritt lånetest – 4 frågor, personliga låneförslag." },
      { property: "og:type", content: "website" },
    ],
  }),
  component: LanetestPage,
});

const purposes = [
  { id: "samla", label: "Samla lån", icon: Layers },
  { id: "renovering", label: "Renovering", icon: Hammer },
  { id: "shopping", label: "Shopping", icon: ShoppingBag },
  { id: "fordon", label: "Fordon", icon: Car },
  { id: "resa", label: "Resa", icon: Plane },
  { id: "ovrigt", label: "Övrigt", icon: MoreHorizontal },
];

function formatKr(n: number) {
  return new Intl.NumberFormat("sv-SE").format(n) + " kr";
}

function LanetestPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState(50000);
  const [purpose, setPurpose] = useState("samla");
  const [income, setIncome] = useState("");
  const [remarks, setRemarks] = useState<"nej" | "ja">("nej");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const pct = (Math.max(0, amount - 1000) / (800000 - 1000)) * 100;

  function submit() {
    try {
      sessionStorage.setItem(
        "laneguide-form",
        JSON.stringify({ amount, purpose, income, remarks, email, phone, source: "lanetest" }),
      );
    } catch {}
    navigate({ to: "/resultat", search: { a: amount, p: purpose, r: remarks } as any });
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-elevated sm:p-10">
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-primary">
            Steg {step} av 3
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-primary sm:text-4xl">
            Vad vill du ha för lån?
          </h1>
          <p className="mt-2 text-sm text-foreground/70">
            Svara på några snabba frågor så matchar vi dig med rätt långivare. Ingen UC-förfrågan görs innan du själv väljer att gå vidare.
          </p>

          {step === 1 && (
            <div className="mt-8 space-y-8">
              <div>
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-semibold text-foreground">
                    Hur mycket vill du låna?
                  </label>
                  <div className="flex items-center gap-2">
                    <AmountInput
                      value={amount}
                      onChange={setAmount}
                      max={800000}
                      min={0}
                      className="w-32 rounded-lg border border-input bg-card px-3 py-1.5 text-right font-display font-extrabold text-primary outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                    />
                    <span className="text-sm font-semibold text-foreground/70">kr</span>
                  </div>
                </div>
                <input
                  type="range"
                  min={0}
                  max={800000}
                  step={1000}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  style={{ ["--val" as any]: `${pct}%` }}
                  className="range-slider mt-3 w-full"
                />
                <div className="mt-1 flex justify-between text-[11px] font-medium text-muted-foreground">
                  <span>{formatKr(0)}</span>
                  <span>{formatKr(800000)}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-foreground">Vad behöver du lånet till?</label>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {purposes.map((p) => {
                    const Icon = p.icon;
                    const active = purpose === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPurpose(p.id)}
                        className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 text-center transition-all ${
                          active
                            ? "border-primary bg-primary-soft text-primary shadow-[0_0_0_3px_color-mix(in_oklab,var(--primary)_15%,transparent)]"
                            : "border-border bg-card hover:border-primary/40"
                        }`}
                      >
                        <Icon className={`h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                        <span className={`text-xs font-semibold ${active ? "text-primary" : "text-foreground"}`}>
                          {p.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-4 text-base font-bold text-primary-foreground transition-all hover:brightness-110"
              >
                Nästa <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="mt-8 space-y-6">
              <div>
                <label className="mb-2 block text-sm font-semibold text-foreground">
                  Din månadsinkomst (före skatt)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    value={income}
                    onChange={(e) => setIncome(e.target.value)}
                    placeholder="35 000"
                    className="w-full rounded-xl border border-input bg-card px-4 py-3 text-[15px] outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  />
                  <span className="text-sm font-semibold text-foreground/70">kr</span>
                </div>
              </div>

              <div>
                <div className="mb-2 text-sm font-semibold text-foreground">
                  Har du betalningsanmärkningar?
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(["nej", "ja"] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setRemarks(v)}
                      className={`rounded-xl border py-3 text-sm font-semibold transition-all ${
                        remarks === v
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card text-foreground hover:border-primary/40"
                      }`}
                    >
                      {v === "nej" ? "Nej" : "Ja"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 rounded-xl border border-border bg-card px-5 py-4 text-sm font-semibold text-foreground hover:bg-muted"
                >
                  <ArrowLeft className="h-4 w-4" /> Tillbaka
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!income}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-4 text-base font-bold text-primary-foreground transition-all hover:brightness-110 disabled:opacity-50"
                >
                  Nästa <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="mt-8 space-y-5">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground">E-postadress</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="anna@example.se"
                  className="w-full rounded-xl border border-input bg-card px-4 py-3 text-[15px] outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground">Mobilnummer</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="070-123 45 67"
                  className="w-full rounded-xl border border-input bg-card px-4 py-3 text-[15px] outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setStep(2)}
                  className="flex items-center gap-2 rounded-xl border border-border bg-card px-5 py-4 text-sm font-semibold text-foreground hover:bg-muted"
                >
                  <ArrowLeft className="h-4 w-4" /> Tillbaka
                </button>
                <button
                  onClick={submit}
                  disabled={!email || !phone}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cta py-4 text-base font-bold text-cta-foreground transition-all hover:brightness-105 disabled:opacity-50"
                >
                  Se mina låneförslag <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
        <FaqSection />
      </main>
      <Footer />
    </div>
  );
}
